import { useEffect, useMemo, useState, useContext, useRef } from 'react';
import './PostComposer.css';
import { AuthContext } from '../AuthProvider';

function PostComposer({ userEmail, onSignOut, backendUrl }) {

  const { user } = useContext(AuthContext);

  const [text, setText] = useState('');
  const [images, setImages] = useState([]);
  const [expireAt, setExpireAt] = useState('');
  const [status, setStatus] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleImagesChange = (event) => {
    const files = Array.from(event.target.files);
    const mapped = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      id: `${file.name}-${file.lastModified}`,
    }));

    setImages(mapped);
    event.target.value = '';
  };

  const handleRemove = (id) => {
    setImages((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!text.trim()) {
      setStatus('Please enter text for your post.');
      return;
    }

    if (!images.length) {
      setStatus('Please attach at least one image.');
      return;
    }

    try {
      setUploading(true);
      setStatus('Sending Post...');

      const formData = new FormData();
      formData.append("title", text);
      formData.append("userId", user.uid);
      formData.append("userEmail", user.email);

      if (expireAt) {
        formData.append("expiresAt", new Date(expireAt).toISOString());
      }

      images.forEach((img) => {
        formData.append("images", img.file);
      });

      const response = await fetch(`${backendUrl}/create-post`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Post sending failed");
      }

      setStatus('Post sent successfully ');
      setText('');
      setImages([]);
      setExpireAt('');

    } catch (err) {
      console.error(err);
      setStatus('Post sending failed ');
    } finally {
      setUploading(false);
    }
  };

  useEffect(
    () => () => {
      images.forEach((item) => URL.revokeObjectURL(item.preview));
    },
    [images]
  );

  const previewGrid = useMemo(
    () =>
      images.map((item) => (
        <div key={item.id} className="preview-card">
          <img src={item.preview} alt={item.file.name} />
          <button
            type="button"
            className="ghost"
            onClick={() => handleRemove(item.id)}
            disabled={uploading}
          >
            Remove
          </button>
        </div>
      )),
    [images, uploading]
  );

  return (
    <div className="card post-card">
      <form className="composer-form" onSubmit={handleSubmit}>

        <label className="composer-field">
          <span>Post text</span>
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            rows={4}
            disabled={uploading}
          />
        </label>

        <label className="composer-field">
          <span>Expire At</span>
          <input
            type="datetime-local"
            value={expireAt}
            onChange={(e) => setExpireAt(e.target.value)}
            disabled={uploading}
          />
        </label>

        <label className="composer-field">
          <span>Images</span>
          <div className="file-chooser">
            <button
              type="button"
              className="ghost"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Choose image'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleImagesChange}
              disabled={uploading}
              className="visually-hidden-input"
            />
            <p className="muted small file-chooser__summary">
              {images.length
                ? `${images.length} selected ${images.slice(0, 2).map((img) => img.file.name).join(', ')}${images.length > 2 ? '...' : ''}`
                : 'No images selected'}
            </p>
          </div>
        </label>

        {images.length > 0 && (
          <section className="preview-grid">
            {previewGrid}
          </section>
        )}

        {status && <p className="status">{status}</p>}

        <button type="submit" disabled={uploading}>
          {uploading ? "Uploading..." : "Post"}
        </button>

      </form>
    </div>
  );
}

export default PostComposer;
